import { useState } from 'react'
import { PlusLg, CheckLg, ArrowUp, ArrowDown } from 'react-bootstrap-icons'
import { useData } from '../store.jsx'
import { PageHeader, Panel, Stat, FormModal, RowActions } from '../components/ui.jsx'
import { entries, netWorth } from '../lib/finance.js'
import { money, todayISO } from '../lib/format.js'
import { add, setAt, removeAt, updateAt } from '../lib/firebase.js'

const FIELDS = [
  { name: 'name', label: 'Item', required: true },
  { name: 'price', label: 'Price', type: 'number', step: '0.01', col: 'col-6' },
  { name: 'link', label: 'Link', type: 'url', col: 'col-6' },
  { name: 'note', label: 'Note' },
]

export default function Wishlist() {
  const data = useData()
  const [edit, setEdit] = useState(null)
  const items = entries(data.wishlist).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  const open = items.filter((i) => !i.boughtAt)
  const bought = items.filter((i) => i.boughtAt)
  const cash = netWorth(data, todayISO()).byCategory.cash || 0
  const total = open.reduce((s, i) => s + (i.price || 0), 0)

  async function save(values) {
    const { id, ...rest } = edit
    if (id) await setAt(`wishlist/${id}`, { ...rest, ...values })
    else await add('wishlist', { ...values, order: items.length ? Math.max(...items.map((i) => i.order ?? 0)) + 1 : 0 })
  }

  async function move(i, dir) {
    const j = i + dir
    if (j < 0 || j >= open.length) return
    await updateAt('wishlist', { [`${open[i].id}/order`]: open[j].order ?? j, [`${open[j].id}/order`]: open[i].order ?? i })
  }

  let running = 0
  return (
    <>
      <PageHeader title="Planned purchases" subtitle="Ordered by priority. The running total shows what your cash covers.">
        <button className="btn btn-primary" onClick={() => setEdit({})}><PlusLg /> Item</button>
      </PageHeader>
      <div className="row g-3 mb-3">
        <div className="col-6 col-lg-3"><Stat label="Planned total" value={money(total)} hint={`${open.length} items`} /></div>
        <div className="col-6 col-lg-3"><Stat label="Cash available" value={money(cash)} /></div>
      </div>
      <Panel>
        {open.length ? (
          <div className="table-wrap"><table className="table align-middle">
            <thead><tr><th style={{ width: 70 }}>#</th><th>Item</th><th className="text-end">Price</th><th className="text-end">Running total</th><th /></tr></thead>
            <tbody>
              {open.map((it, i) => {
                running += it.price || 0
                return (
                  <tr key={it.id}>
                    <td className="text-nowrap">
                      <button className="icon-btn" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp /></button>
                      <button className="icon-btn" onClick={() => move(i, 1)} disabled={i === open.length - 1}><ArrowDown /></button>
                    </td>
                    <td>{it.link ? <a href={it.link} target="_blank" rel="noreferrer">{it.name}</a> : it.name}
                      {it.note && <div className="small text-body-secondary">{it.note}</div>}</td>
                    <td className="num">{it.price != null ? money(it.price) : '–'}</td>
                    <td className={`num ${running > cash ? 'text-body-secondary' : 'pos'}`}>{money(running)}</td>
                    <td className="text-end text-nowrap">
                      <button className="icon-btn" title="Mark as bought" onClick={() => updateAt(`wishlist/${it.id}`, { boughtAt: todayISO() })}><CheckLg /></button>
                      <RowActions onEdit={() => setEdit(it)} onDelete={() => removeAt(`wishlist/${it.id}`)} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table></div>
        ) : <div className="empty">Nothing planned.</div>}
        {bought.length > 0 && (
          <details className="mt-3 small"><summary className="text-body-secondary">Bought ({bought.length})</summary>
            <ul className="mt-2">{bought.map((b) => <li key={b.id}>{b.name} · {b.price != null ? money(b.price) : ''} · {b.boughtAt}
              <button className="icon-btn ms-1" onClick={() => updateAt(`wishlist/${b.id}`, { boughtAt: null })}>undo</button></li>)}</ul>
          </details>
        )}
      </Panel>
      {edit && <FormModal title={edit.id ? 'Edit item' : 'New item'} fields={FIELDS} initial={edit} onSubmit={save} onClose={() => setEdit(null)} />}
    </>
  )
}
